import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const STORY_IMG =
  "https://customer-assets.emergentagent.com/job_b9e021d3-e729-4e4e-a5aa-aa23768687ba/artifacts/agn0hs7h_dc3ca59b-eebd-4c1d-9c89-86b86059a7cd.jpg";

const CHAPTERS = [
  {
    title: "Leaving Romania",
    image: null,
    body: [
      "In 2021, I left Romania with mostly nothing but some clothes. My first job in Germany ended quickly, and with the second one, after six months, I went straight into the raw life. I started traveling intensely, learning a strangely spontaneous yet beautiful art: how to create everything out of nothing, even when my pockets were almost empty. I passed through over 200 cities across Europe and all of this changed me in ways nothing else could. I grew incredibly.",
      "But on the surface, it was a continuous adventure, a wild one. Deep down, however, it was a run. I was desperately trying to fill an inner void I didn't understand, and in that race, I ended up doing things that hurt me.",
    ],
  },
  {
    title: "The dark place",
    image: null,
    body: [
      "By 2024, I was in Switzerland. I was in a very dark mental, emotional, and spiritual place. I had started having a drug problem; what used to be for fun had become a constant in my life. I felt like I was losing control and slowly stepping into a place I no longer knew how to escape.",
      "In my final moment, I asked for help. I prayed. I remember my Instagram was deactivated at the time, but a thought told me to activate it. And the very first thing that popped up when I opened it was Ayahuasca.",
    ],
  },
  {
    title: "The awakening",
    image: null,
    body: [
      "Out of that darkness, a light was lit: I left for Peru. There, I was introduced to the shamanic world and shamanic medicine. It was my spiritual awakening.",
    ],
  },
  {
    title: "The obsessive search",
    image: null,
    body: [
      "From that moment on, my life turned into an obsessive search. I became obsessed with healing whatever was hurting me. I did every possible therapy — if you were a therapist and promised a way, a technique, I came to you. I was guided by a single thought: “If there is something to be found there for me, I'm going to go and take it.” I learned immensely from that period and got to know a lot, but I didn't find the answers I was looking for.",
      "Going through this filter, I understood that most of today's so-called “spiritual world” is still very far from true spirituality. For me, spirituality has been radically simplified: it means pure love.",
    ],
  },
  {
    title: "The path I hold",
    image: null,
    body: [
      "So I walked away from there and chose to follow a single path: shamanism. I continued going to Peru, working with the plants, and pursuing my initiation as a shaman with two shamanic lineages — the Rainbow lineage and the Shipibo-Conibo ayahuasqueros. Now, I have an extraordinary master from the Shipibo-Conibo lineage. With him, I am learning how energy truly works and what real healing means, way beyond theories.",
      "This is where I find myself. This is my calling and the purest thing inside my heart. In my essence, this is what I want to do for the rest of my life: to learn, to know myself, and to be able to bring light into the world through who I am.",
      "Thank you for walking this journey with me. I love you all.",
    ],
  },
];

export default function StoryPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="story-page" className="relative z-10 min-h-screen">
      <header className="sticky top-0 z-50 bg-[#06040A]/80 backdrop-blur-xl border-b border-[#2C223A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="story-page-logo">
            <img src="/ovidiu-logo.png" alt="Ovidiu" className="h-12 md:h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="story-back-home"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-[#A59DAB] hover:text-[#D6B56D] transition-colors"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-6 text-outline-strong">
            My Story
          </p>
          <h1 className="font-heading font-light text-5xl md:text-7xl text-[#EBE4D8] leading-[1.02] text-outline-strong">
            The long <span className="italic text-[#D6B56D]">way home</span>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="mb-20 md:mb-24 grid md:grid-cols-12 gap-10 md:gap-14 items-center"
        >
          <div className="relative md:col-span-5">
            <img
              src={STORY_IMG}
              alt="Little me"
              className="relative w-full max-h-[540px] object-contain mx-auto"
            />
          </div>
          <div className="md:col-span-7">
            <p
              data-testid="story-intro-text"
              className="text-[#EBE4D8] leading-relaxed text-base md:text-lg font-light text-outline-strong"
            >
              I am a soul that came from far beyond, from the vastness of the universe, having worn and shed human form countless times. This lifetime is a special one for me. I chose a path filled with challenges — one that calls for balance and inner harmony. I will always say this: I have lived a beautiful life, but never an easy one. No one can truly understand where I come from or what it took to become the person I am today.
            </p>
          </div>
        </motion.div>

        <article className="space-y-20 md:space-y-28">
          {CHAPTERS.map((ch, i) => {
            const hasImage = !!ch.image;
            return (
              <motion.section
                key={ch.title}
                data-testid={`story-chapter-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7 }}
                className={
                  hasImage
                    ? "grid md:grid-cols-12 gap-8 md:gap-12 items-start"
                    : "max-w-3xl mx-auto"
                }
              >
                <div
                  className={
                    hasImage
                      ? `md:col-span-7 ${
                          i % 2 === 1 ? "md:col-start-6 md:order-2" : ""
                        }`
                      : ""
                  }
                >
                  <h2 className="font-heading italic text-3xl md:text-4xl text-[#D6B56D] mb-5 text-outline-strong">
                    {ch.title}
                  </h2>
                  <div className="space-y-4 text-[#EBE4D8] leading-relaxed text-base md:text-lg font-light text-outline-strong">
                    {ch.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </div>

                {hasImage && (
                  <div
                    className={`md:col-span-5 ${
                      i % 2 === 1 ? "md:col-start-1 md:order-1" : ""
                    }`}
                  >
                    <img
                      src={ch.image}
                      alt={ch.title}
                      loading="lazy"
                      className="block w-full h-auto"
                    />
                  </div>
                )}
              </motion.section>
            );
          })}
        </article>

        <div className="mt-20 text-center">
          <Link
            to="/"
            data-testid="story-bottom-home"
            className="inline-flex items-center gap-3 px-9 py-3.5 rounded-full border border-[#D6B56D]/60 text-[#EBE4D8] text-sm tracking-widest uppercase hover:bg-[#D6B56D]/10 hover:border-[#D6B56D] transition-all duration-300"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </main>
    </div>
  );
}
